import { useMemo } from 'react';
import { compareWithNameTiebreaker } from '@macmillan-learning/gradebook-shared';

/**
 * Creates a placeholder row for empty gradebooks (no students but has assignments).
 * Matches V1 behavior where the empty gradebook shows a single row with "missing"
 * grade indicators (dashes) for assignments and blank cells for category totals.
 *
 * @param {Array} assignments - Array of assignment objects
 * @param {Array} categories - Array of category objects
 * @returns {Object} A placeholder row object with missing grades and blank category totals
 */
export function createPlaceholderRow(assignments, categories) {
  // Create placeholder grades marked as "missing" for each assignment (shows dash)
  const grades = {};
  assignments.forEach((assignment) => {
    grades[String(assignment.id)] = {
      studentId: 'placeholder',
      assignmentId: String(assignment.id),
      missing: true,
      isAssigned: false,
    };
  });

  // Create placeholder category totals marked as "blank" (shows empty)
  const categoryTotals = {};
  categories.forEach((category) => {
    categoryTotals[category.id] = {
      blank: true,
    };
  });

  return {
    student: {
      id: 'placeholder',
      name: '',
      firstName: '',
      lastName: '',
      email: '',
    },
    courseTotal: 0,
    coursePointsEarned: 0,
    coursePointsPossible: 0,
    categoryTotals,
    grades,
    isPlaceholderRow: true,
  };
}

/**
 * Hook to process gradebook data into table-ready format
 *
 * Transforms raw gradebook data (students, calculations, averages) into
 * structured row data suitable for TanStack Table rendering.
 *
 * @param {Object} options
 * @param {Object} options.data - Gradebook data containing students, assignments, categories
 * @param {Object} options.calculations - Pre-calculated grade data from reduxDataAdapter
 * @param {Array} options.averages - Assignment averages from Redux
 * @returns {Object} { rows, averageRow, categoryGroups }
 */
export function useGradebookData({ data, calculations, averages }) {
  // Process student data into row format
  const { rows, averageRow } = useMemo(() => {
    let studentRows;

    if (!calculations) {
      // Fallback when calculations aren't available
      studentRows = data.students.map((student) => ({
        student,
        courseTotal: 0,
        categoryTotals: {},
        grades: {},
      }));
    } else {
      // Use pre-calculated data from reduxDataAdapter
      studentRows = data.students.map((student) => {
        const calc = calculations.studentCalculations[student.id];
        if (!calc) {
          console.warn(
            `[useGradebookData] No calculations found for student ${student.id}`,
          );
          return {
            student,
            courseTotal: 0,
            coursePointsEarned: 0,
            coursePointsPossible: 0,
            categoryTotals: {},
            grades: {},
          };
        }

        return {
          student,
          courseTotal: calc.courseTotal,
          // Include course points for points mode display
          coursePointsEarned: calc.coursePointsEarned,
          coursePointsPossible: calc.coursePointsPossible,
          categoryTotals: calc.categoryTotals,
          grades: calc.gradesByAssignment,
        };
      });
    }

    // V1 empty gradebook shows a single row with "missing" grade indicators
    // when there are no students but there are assignments
    if (studentRows.length === 0 && data.hasAssignments) {
      studentRows = [createPlaceholderRow(data.assignments, data.categories)];
    }

    // Build averageRow from Redux averages (matches V1 behavior)
    // Build a lookup map for assignments to check grading type
    const assignmentMap = new Map(
      data.assignments.map((a) => [String(a.id), a]),
    );

    // Build grades object from Redux averages array
    const averageGrades = {};
    if (averages && averages.length > 0) {
      averages.forEach((avg) => {
        const assignment = assignmentMap.get(String(avg.assignmentId));
        const isUngraded = assignment?.isUngraded || false;

        averageGrades[String(avg.assignmentId)] = {
          studentId: 'average',
          assignmentId: String(avg.assignmentId),
          score:
            avg.pointsPossible > 0
              ? (avg.pointsEarned / avg.pointsPossible) * 100
              : 0,
          pointsEarned: avg.pointsEarned,
          pointsPossible: avg.pointsPossible,
          isUngraded,
          numCompleted: avg.numCompleted || 0,
          tool: assignment?.tool || null,
        };
      });
    }

    const avgRow = {
      student: { id: 'average', name: 'Average', email: '' },
      courseTotal: 0, // V1 shows empty for course total in average row
      categoryTotals: {}, // V1 shows empty for category totals in average row
      grades: averageGrades,
    };

    return { rows: studentRows, averageRow: avgRow };
  }, [data, calculations, averages]);

  // Group assignments by category for column organization
  const categoryGroups = useMemo(() => {
    // Helper to get effective due date for sorting (matches V1 sortAssignmentsByDueDate)
    const getEffectiveDueDate = (assignment) => {
      // Use assignment_due_at if available
      if (assignment.assignment_due_at) {
        return assignment.assignment_due_at;
      }
      // Fall back to exception due date (matches V1 behavior)
      const assignmentException = assignment.Exceptions?.find(
        (ex) =>
          ex.assignment_id === assignment.id &&
          ex.exception_type === 'assignment',
      );
      return assignmentException?.exception_value?.due_date || null;
    };

    const groups = new Map();
    data.categories.forEach((category) => {
      const assignments = data.assignments
        .filter((a) => a.category === category.id)
        .sort((a, b) => {
          const dateA = getEffectiveDueDate(a);
          const dateB = getEffectiveDueDate(b);
          // Handle null dates - put them at the end
          if (!dateA && !dateB) return compareWithNameTiebreaker(0, a, b);
          if (!dateA) return 1;
          if (!dateB) return -1;
          const dateCompare =
            new Date(dateA).getTime() - new Date(dateB).getTime();
          return compareWithNameTiebreaker(dateCompare, a, b);
        });
      groups.set(category.id, assignments);
    });
    return groups;
  }, [data.categories, data.assignments]);

  return { rows, averageRow, categoryGroups };
}

export default useGradebookData;
